import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import type { FinancialSummary as FinancialSummaryType, OrderInfo } from '../types/budget';
import { formatCurrency } from '../utils/calculations';

interface FinancialSummaryProps {
  orderInfo: OrderInfo;
  financialSummary: FinancialSummaryType;
}

export const FinancialSummary: React.FC<FinancialSummaryProps> = ({
  orderInfo,
  financialSummary,
}) => {
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Financial Summary
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Order Values */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Order Information
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Order Value:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(orderInfo.orderValue)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Export Value:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(orderInfo.exportValue)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Order Quantity:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {orderInfo.orderQty.toLocaleString()} pcs
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Export Quantity:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {orderInfo.expQty.toLocaleString()} pcs
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Cost Analysis */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" color="secondary" gutterBottom>
              Cost Analysis
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Total Cost:</Typography>
              <Typography variant="body2" fontWeight="bold" color="error">
                {formatCurrency(financialSummary.totalCost)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Cost Percentage:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {financialSummary.totalCostPercentage.toFixed(2)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Price per Piece:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(orderInfo.pricePerPc)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Profit Analysis */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" color="success.main" gutterBottom>
              Profit Analysis
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Profit Amount:</Typography>
              <Typography 
                variant="body2" 
                fontWeight="bold" 
                color={financialSummary.profit >= 0 ? 'success.main' : 'error.main'}
              >
                {formatCurrency(financialSummary.profit)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Profit Percentage:</Typography>
              <Typography 
                variant="body2" 
                fontWeight="bold"
                color={financialSummary.profitPercentage >= 0 ? 'success.main' : 'error.main'}
              >
                {financialSummary.profitPercentage.toFixed(2)}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Profit per Piece:</Typography>
              <Typography 
                variant="body2" 
                fontWeight="bold"
                color={financialSummary.profit >= 0 ? 'success.main' : 'error.main'}
              >
                {formatCurrency(financialSummary.profit / orderInfo.expQty)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Production Metrics */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" color="info.main" gutterBottom>
              Production Metrics
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Final CM per Dozen:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(financialSummary.finalCmPerDz)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Final MC Earning:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(financialSummary.finalMcEarning)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Contribution Margin per Unit:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(financialSummary.contributionMarginPerUnit)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Breakeven Units:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {financialSummary.breakevenUnits.toLocaleString()} pcs
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">SMV:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {orderInfo.smv || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Production/Hour:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {orderInfo.prodHour || 'N/A'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Summary Overview */}
      <Card variant="outlined" sx={{ bgcolor: 'grey.50', mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Budget Overview
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              <strong>Buyer:</strong> {orderInfo.buyer} | <strong>Style:</strong> {orderInfo.style} | <strong>Item:</strong> {orderInfo.item}
            </Typography>
            <Typography variant="body1">
              <strong>Margin:</strong> 
              <span style={{ 
                color: financialSummary.profitPercentage >= 0 ? 'green' : 'red',
                fontWeight: 'bold',
                marginLeft: '8px'
              }}>
                {financialSummary.profitPercentage.toFixed(2)}%
              </span>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Paper>
  );
};
